<template>
  <main id="main-content" class="page page--resume">
    <div class="page__content">
      <Heading title="Resume" hasDivider="true" />

      <p
        class="page__intro"
      >Your newsfeed for all things Nicola. New stuff at the top, old stuff gets pushed down over time.</p>

      <div class="sheet sheet--note">
        <h2>Hi! Nice to meet you, I'm Nicola.</h2>
        <p>I’m a hybrid designer and developer with an obsession for clean, intuitive design. A creative “unicorn” who delights in iterating design ideas and seeing them come to life. In my 12 years of experience, I've designed digital experiences in a variety of contexts; at agencies and design studios, as well as with start-ups, and in-house teams. You name it, I've designed it: brand guidelines, UI interfaces, coffee cups, even a COVID-19 planning tool. If it’s something I've not yet come across, then bring on the challenge!</p>

        <p>
          Want to hire me? Great –
          <router-link to="/contact">let’s talk</router-link>.
        </p>
      </div>

      <div class="sheet">
        <h2 class="entry__title">Hybrid Designer &amp; Front End Developer</h2>

        <Posting
          heading="York &amp; Chapel"
          detail="Vancouver, BC"
          date="Feb 2019 - Present"
          profile="yc-icon.jpg"
          url
        />

        <div class="entry__body">
          <h5>UX/UI</h5>
          <ul>
            <li>
              Design lead on two SaaS applications:
              <a
                target="_blank"
                href="https://mycoraapp.com/"
              >CORA</a>—a COVID-19 risk assessment planning tool, and the redesign of Fiitfu CRM—a growth tool for Network Marketers;
            </li>
            <li>
              Design lead for Intuit’s
              <a
                target="_blank"
                href="https://quickbooksconnect.com/"
              >QuickBooks Connect</a> where I work within and contribute to their established design system. I also collaborate directly with Intuit’s Project Managers and Head of Design to ensure our work aligns;
            </li>
            <li>Provide creative direction and concept development for UX/UI projects for major international clients, including Intuit and Logitech;</li>
            <li>Translate stakeholder goals, user research, and complex user flows into simple, intuitive UIs;</li>
            <li>Develop, test and iterate user journeys, personas and site maps to communicate and discover user needs;</li>
            <li>Plan and deliver production-ready prototypes for user testing sessions;</li>
            <li>Work cross-functionally, ensuring meaningful collaboration between Design and Development teams;</li>
            <li>Mentor and provide creative guidance to the Junior and Middleweight Designers within the team.</li>
          </ul>
          <h5>Development</h5>
          <ul>
            <li>
              Lead Front End Developer for
              <a href="https://mycoraapp.com/" target="_blank">CORA</a> which went from concept to MVP launch in 6 weeks. I have been able to define my own tasks, make adjustments to the scope based on findings from our MVP, and work with Back End Developers to build and ship out new features. Application built in Vue;
            </li>
            <li>
              In October 2020, I designed and developed Intuit's first
              <a
                href="https://quickbooksconnect.com/virtual-event/"
                target="_blank"
              >Virtual Event</a> landing page in 2 rounds of iterations which resulted in 18,500+ registrations within 48 hours of launch;
            </li>
            <li>
              Lead Front End Developer for Quickbooks Connect, Intuit’s high-profile global event
              <a
                href="https://quickbooksconnect.com/"
                target="_blank"
              >websites</a> and apps;
            </li>
            <li>
              Develop custom WordPress and HubSpot themes for a range of highly acclaimed national and global start-ups, including
              <a
                href="https://jaxjox.com/"
                target="_blank"
              >JaxJox</a> &
              <a href="https://www.rti.com/en/" target="_blank">Real-Time Innovations (RTI)</a>;
            </li>
            <li>Oversee production specs, facilitate reviews with stakeholders and make ongoing iterations to the design and code based on user research;</li>
            <li>Collaborate with an international team of developers, coordinating projects and deliverables across time zones.</li>
          </ul>
        </div>
      </div>
      <div class="sheet">
        <h2 class="entry__title">Designer &amp; Front End Developer</h2>

        <Posting
          heading="University of Nottingham"
          detail="Nottingham UK"
          date="Dec 2016 - Nov 2017"
          profile="uon-icon.jpg"
          url
        />

        <div class="entry__body">
          <ul>
            <li>Provided the artistic direction for marketing campaigns, from initial concepts to final product;</li>
            <li>Contributed to the development and ongoing evolution of the Students’ Union web estate ensuring the user experience, digital engagement journeys and brand were considered at all times;</li>
            <li>Acted as Project Lead on two new initiatives, Portland Coffee Co. and the new Students’ Union shop. Developing both brand identities and producing all print and digital collateral within tight timescales;</li>
            <li>Monitored all artwork (produced internally, or by partner stakeholders) to ensure it adheres to brand guidelines.</li>
          </ul>
        </div>
      </div>

      <div class="sheet">
        <h2 class="entry__title">Creative Director</h2>

        <Posting
          heading="Pulse Studios"
          detail="Remote"
          date="Sep 2013 - Jan 2017"
          profile="ps-icon.jpg"
          url
        />

        <div class="entry__body">
          <ul>
            <li>Created branding, illustrations, print and UI design for over 30 start-ups and small businesses including Multi-Let, one of the UK’s leading letting agents;</li>
            <li>Notable projects include the redesign of the ecommerce flow for Company Address which achieved 1127% monthly revenue increase and 231% increase in monthly ecommerce conversion rate over the 9 month period after launch;</li>
            <li>Collaborated with Developer teams and managing a wide variety of cross-media projects;</li>
            <li>Designed and developed intuitive and highly functional user experiences that adapt across multiple devices;</li>
            <li>Documented visual style guides and undertook QA once the sites had been developed;</li>
            <li>Generated bespoke vector graphics and illustrations for use in print and web animations;</li>
            <li>Worked on-site at design agencies as a contractor;</li>
            <li>Liaised with suppliers and co-ordinated production of both print and digital assets.</li>
          </ul>
        </div>
      </div>

      <div class="sheet">
        <h2 class="entry__title">Web Designer</h2>

        <Posting
          heading="Nottingham Trent University"
          detail="Nottingham, UK"
          date="Jul 2008 - Sep 2013"
          profile="ntu-icon.jpg"
          url
        />

        <div class="entry__body">
          <ul>
            <li>Worked closely with Project Managers and Developers to provide visually-compelling and highly-usable web applications and sites in accordance with the NTU brand;</li>
            <li>Provided specifications for design, layout and usability of the university’s websites, dynamic application software, enterprise search and other web developments;</li>
            <li>Provided design and usability expertise for input into business and technical specifications and documentation for projects;</li>
            <li>Ensured client consultancy on design and usability requirements and the review of specifications prior to build;</li>
            <li>Designed and generated fully-interactive HTML prototypes and click-throughs which were used for client presentations, mock-ups, as well as for user-testing studies;</li>
            <li>Singlehandedly implemented and developed WordPress as the primary blogging platform for NTU staff, students and researchers, for which I received the Contribution Increment award for my 2011/12 performance.</li>
          </ul>
        </div>
      </div>

      <div class="sheet">
        <h2 class="entry__title">BA (Hons) Multimedia Design</h2>
        <Posting
          heading="Nottingham Trent University"
          detail="Nottingham, UK"
          date="Sep 2005 - Jul 2008"
          profile="ntu-icon.jpg"
          url
        />

        <div class="entry__body">
          <p>A self-initiated, interdisciplinary program combining studies in web design, development and animation. Graduated with First Class Honours.</p>
        </div>
      </div>

      <div class="sheet">
        <h2
          class="entry__title"
        >A-levels in Product Design, Psychology, English Language and Literature</h2>
        <Posting
          heading="Ferndown Upper School"
          detail="Ferndown, UK"
          date="Sep 2003 - Jul 2005"
          profile="fus-icon.jpg"
          url
        />
      </div>

      <div class="sheet">
        <h2 class="entry__title">Skills</h2>

        <div class="entry__body">
          <h5>Design</h5>
          <ul>
            <li>User-centered design principles and practices</li>
            <li>Branding &amp; illustration</li>
            <li>Ideation &amp; workflows</li>
            <li>Low to high fidelity designs &amp; prototyping</li>
            <li>UI &amp; interaction design</li>
            <li>Responsive design</li>
            <li>Style guides &amp; design systems</li>
          </ul>

          <h5>Code</h5>
          <ul>
            <li>Strong - Modern HTML, CSS/SASS</li>
            <li>Familiar - JS/ES6, Vue, PHP, Git &amp; working in terminal, NPM</li>
            <li>Learning - React</li>
          </ul>

          <h5>Tools</h5>
          <ul>
            <li>Figma, Sketch, Invision, Adobe Creative Suite, Atom, VS Code</li>
          </ul>

          <p>I have worked with many different prototyping tools, frameworks and software – to avoid a full laundry list above, I’ve kept it to just my favorites. If you use a different prototyping tool that works well in your established workflow, I would be happy to learn it.</p>
        </div>
      </div>
    </div>
  </main>
</template>
<script>
import Heading from "@/components/Heading";
import Posting from "@/components/Posting";
export default {
  name: "Resume",
  metaInfo: {
    title: "Nicola Bolton Resume",
    titleTemplate: "%s | HEY"
  },
  components: {
    Heading,
    Posting
  }
};
</script>
